/**
 * UsageStats - Admin/Developer view for AI response details
 *
 * Collapsed: Only timestamp + "Ver detalles"
 * Expanded: All technical information (model, tokens, costs, flow, RAG, citations)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Cpu, DollarSign, Clock, Zap, Copy, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  useUsageStatsFormatting,
  useUsageStatsCalculations,
  FlowDiagram,
  UsageStatsRAGSection,
  UsageStatsCitationsSection,
} from './stats';
import type { UsageStatsProps } from './stats/types/stats.types';

const UsageStats: React.FC<UsageStatsProps> = ({
  stats,
  citations,
  onCitationClick,
  ragStats,
  aiFlowTrace,
  timestamp,
  isCollapsed: externalIsCollapsed,
  onToggleCollapse,
  hideHeader,
}) => {
  const { theme } = useTheme();
  const [copiedStats, setCopiedStats] = useState(false);

  if (!stats) return null;

  const { formatDuration, formatTime, formatNumber } = useUsageStatsFormatting();
  const { timingRows, totalPipelineSeconds } = useUsageStatsCalculations({
    timingBreakdown: stats.timing_breakdown,
  });

  const isCollapsed = externalIsCollapsed ?? true;
  const summaryDurationSeconds = totalPipelineSeconds ?? stats.elapsed_time_seconds;

  const modelName = (ragStats?.model_configuration?.model || stats?.model_used || '')
    .replace('gpt-4o-', '')
    .replace('-preview', '');
  const temperature = ragStats?.model_configuration?.temperature;

  const handleCopyStats = () => {
    const statsText = `Modelo: ${modelName}${temperature !== undefined ? ` (t=${temperature})` : ''}
Tokens: ${formatNumber(stats.total_tokens)} (In: ${formatNumber(
      stats.prompt_tokens
    )}, Out: ${formatNumber(stats.completion_tokens)})
Costo: $${formatNumber(stats.cost_clp)} CLP
Tiempo: ${formatTime(summaryDurationSeconds)}`;
    navigator.clipboard.writeText(statsText);
    setCopiedStats(true);
    setTimeout(() => setCopiedStats(false), 2000);
  };

  return (
    <div className={`space-y-2`}>
      {/* Summary Line - Only timestamp + Ver detalles (hidden when parent handles toggle) */}
      {!hideHeader && (
        <button
          onClick={onToggleCollapse}
          className={`w-full flex items-center justify-between text-[10px] transition-colors group ${theme.colors.text.secondary}/70 hover:${theme.colors.text.secondary}`}
        >
          <span className={`opacity-70 ${theme.colors.text.secondary}`}>
            {timestamp?.toLocaleTimeString('es-CL', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span
            className={`flex items-center gap-1 opacity-60 group-hover:opacity-100 ${theme.colors.text.secondary}`}
          >
            {isCollapsed ? 'Ver detalles' : 'Ocultar'}
            {isCollapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </span>
        </button>
      )}

      {/* Expanded Details */}
      {!isCollapsed && (
        <div className={`pt-2 border-t ${theme.colors.border.primary}/30 space-y-3`}>
          {/* Section 1: Quick Summary */}
          <div
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] ${theme.colors.text.secondary}`}
          >
            {modelName && (
              <span className="flex items-center gap-1">
                <Cpu className="w-3 h-3" />
                <span className={`${theme.colors.text.primary} font-mono`}>{modelName}</span>
                {temperature !== undefined && <span>t={temperature}</span>}
              </span>
            )}
            <span>
              <span className={theme.colors.text.primary}>{formatNumber(stats.total_tokens)}</span>{' '}
              tokens
            </span>
            <span>
              <span className={theme.colors.text.primary}>${formatNumber(stats.cost_clp)}</span> CLP
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span className={theme.colors.text.primary}>
                {formatTime(summaryDurationSeconds)}
              </span>
            </span>
          </div>

          {/* Section 2: Tokens & Costs */}
          <div className={`p-2 rounded-lg ${theme.colors.bg.secondary}/50`}>
            <div className="flex items-center justify-between mb-2">
              <div
                className={`flex items-center gap-1.5 ${theme.colors.text.primary} text-[10px] font-medium`}
              >
                <Zap className="w-3 h-3 text-yellow-400" />
                <span>Tokens y Costos</span>
              </div>
              <button
                onClick={handleCopyStats}
                className={`text-[9px] ${theme.colors.text.secondary}/50 hover:${theme.colors.text.secondary} flex items-center gap-1`}
                title="Copiar estadísticas"
              >
                {copiedStats ? (
                  <Check className="w-3 h-3 text-green-400" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[9px]">
              <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                <span>Input tokens:</span>
                <span className={theme.colors.text.primary}>
                  {formatNumber(stats.prompt_tokens)}
                </span>
              </div>
              <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                <span>Output tokens:</span>
                <span className={theme.colors.text.primary}>
                  {formatNumber(stats.completion_tokens)}
                </span>
              </div>
              <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                <span>Total tokens:</span>
                <span className={`${theme.colors.text.primary} font-medium`}>
                  {formatNumber(stats.total_tokens)}
                </span>
              </div>
              <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                <span>Palabras:</span>
                <span className={theme.colors.text.primary}>{formatNumber(stats.word_count)}</span>
              </div>
              {stats.cost_breakdown && (
                <>
                  <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                    <span>Costo input:</span>
                    <span className={theme.colors.text.primary}>
                      ${formatNumber(stats.cost_breakdown.input_clp || 0)}
                    </span>
                  </div>
                  <div className={`flex justify-between ${theme.colors.text.secondary}`}>
                    <span>Costo output:</span>
                    <span className={theme.colors.text.primary}>
                      ${formatNumber(stats.cost_breakdown.output_clp || 0)}
                    </span>
                  </div>
                </>
              )}
              <div
                className={`flex justify-between ${theme.colors.text.secondary} col-span-2 pt-1 border-t ${theme.colors.border.primary}/20`}
              >
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Costo total:
                </span>
                <span className={`${theme.colors.text.primary} font-medium`}>
                  ${formatNumber(stats.cost_clp)} CLP
                </span>
              </div>
            </div>
          </div>

          {/* Section 3: System Flow */}
          {aiFlowTrace && aiFlowTrace.length > 0 && (
            <div className={`p-2 rounded-lg ${theme.colors.bg.secondary}/50`}>
              <FlowDiagram aiFlowTrace={aiFlowTrace} theme={theme} />
            </div>
          )}

          {/* Section 4: Timing Breakdown */}
          {timingRows.length > 0 && (
            <div className={`p-2 rounded-lg ${theme.colors.bg.secondary}/50`}>
              <div
                className={`flex items-center gap-1.5 ${theme.colors.text.primary} text-[10px] font-medium mb-2`}
              >
                <Clock className="w-3 h-3 text-blue-400" />
                <span>Desglose de Tiempos</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[9px]">
                {timingRows
                  .filter((row) => row.value && (row.isGroup || row.depth === 0))
                  .slice(0, 8)
                  .map((row) => (
                    <div
                      key={row.id}
                      className={`flex justify-between ${theme.colors.text.secondary}`}
                    >
                      <span>{row.label}:</span>
                      <span className={theme.colors.text.primary}>
                        {formatDuration(row.value!)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Section 5: RAG Stats */}
          {ragStats && (
            <div className={`p-2 rounded-lg ${theme.colors.bg.secondary}/50`}>
              <UsageStatsRAGSection ragStats={ragStats} formatNumber={formatNumber} theme={theme} />
            </div>
          )}

          {/* Section 6: References */}
          <UsageStatsCitationsSection
            citations={citations}
            onCitationClick={onCitationClick}
            theme={theme}
          />
        </div>
      )}
    </div>
  );
};

export default UsageStats;
